(() => {
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const cartAddUrl = () => window.routes?.cart_add_url || `${rootPath()}cart/add`;
  const cartUrl = () => window.routes?.cart_url || `${rootPath()}cart`;

  if (!customElements.get('rd-product-gallery')) {
    class RDProductGallery extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';

        this.viewer = this.querySelector('[data-rd-gallery-viewer]');
        this.mainImage = this.querySelector('[data-rd-gallery-main]');
        this.thumbs = Array.from(this.querySelectorAll('[data-rd-gallery-thumb]'));
        this.current = this.querySelector('[data-rd-gallery-current]');
        this.prevButton = this.querySelector('[data-rd-gallery-prev]');
        this.nextButton = this.querySelector('[data-rd-gallery-next]');

        if (!this.viewer || !this.mainImage) return;

        const activeThumbIndex = this.thumbs.findIndex(
          (thumb) => thumb.getAttribute('aria-current') === 'true'
        );
        const suppliedIndex = Number.parseInt(this.dataset.activeIndex || '0', 10);
        this.activeIndex = activeThumbIndex >= 0
          ? activeThumbIndex
          : Number.isInteger(suppliedIndex) ? suppliedIndex : 0;

        this.pointerStartX = null;
        this.pointerStartY = null;

        this.onThumbClick = this.onThumbClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onPrevClick = () => this.goTo(this.activeIndex - 1);
        this.onNextClick = () => this.goTo(this.activeIndex + 1);

        this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));
        this.prevButton?.addEventListener('click', this.onPrevClick);
        this.nextButton?.addEventListener('click', this.onNextClick);
        this.viewer.addEventListener('keydown', this.onKeydown);
        this.viewer.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.viewer.addEventListener('pointerup', this.onPointerUp, { passive: true });

        this.setActive(this.activeIndex, false);
      }

      disconnectedCallback() {
        this.thumbs?.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
        this.prevButton?.removeEventListener('click', this.onPrevClick);
        this.nextButton?.removeEventListener('click', this.onNextClick);
        this.viewer?.removeEventListener('keydown', this.onKeydown);
        this.viewer?.removeEventListener('pointerdown', this.onPointerDown);
        this.viewer?.removeEventListener('pointerup', this.onPointerUp);
      }

      onThumbClick(event) {
        const index = Number.parseInt(event.currentTarget.dataset.slideIndex || '0', 10);
        this.goTo(index);
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.activeIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.activeIndex + 1);
        }
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse') return;
        this.pointerStartX = event.clientX;
        this.pointerStartY = event.clientY;
      }

      onPointerUp(event) {
        if (this.pointerStartX === null || this.pointerStartY === null) return;

        const deltaX = event.clientX - this.pointerStartX;
        const deltaY = event.clientY - this.pointerStartY;
        this.pointerStartX = null;
        this.pointerStartY = null;

        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.goTo(deltaX < 0 ? this.activeIndex + 1 : this.activeIndex - 1);
      }

      goTo(index) {
        if (!this.thumbs.length) return;
        const lastIndex = this.thumbs.length - 1;
        const targetIndex = index < 0 ? lastIndex : index > lastIndex ? 0 : index;
        this.setActive(targetIndex, true);
      }

      setActive(index, swapImage = true) {
        const thumb = this.thumbs[index];
        if (this.thumbs.length && !thumb) return;
        this.activeIndex = Math.max(0, index);

        if (swapImage && thumb) {
          const src480 = thumb.dataset.image480 || '';
          const src800 = thumb.dataset.image800 || '';
          const src1200 = thumb.dataset.image1200 || '';
          const src1600 = thumb.dataset.image1600 || src1200 || src800 || src480;

          if (src1600) {
            this.mainImage.srcset = [
              src480 && `${src480} 480w`,
              src800 && `${src800} 800w`,
              src1200 && `${src1200} 1200w`,
              src1600 && `${src1600} 1600w`
            ].filter(Boolean).join(', ');
            this.mainImage.src = src1600;
          }

          this.mainImage.alt = thumb.dataset.imageAlt || '';

          const width = Number.parseInt(thumb.dataset.imageWidth || '', 10);
          const height = Number.parseInt(thumb.dataset.imageHeight || '', 10);
          if (Number.isFinite(width) && width > 0) this.mainImage.width = width;
          if (Number.isFinite(height) && height > 0) this.mainImage.height = height;
        }

        this.thumbs.forEach((item, thumbIndex) => {
          const isActive = thumbIndex === this.activeIndex;
          item.classList.toggle('is-active', isActive);
          if (isActive) {
            item.setAttribute('aria-current', 'true');
            item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          } else {
            item.removeAttribute('aria-current');
          }
        });

        if (this.current) this.current.textContent = String(this.activeIndex + 1);
      }
    }

    customElements.define('rd-product-gallery', RDProductGallery);
  }

  if (!customElements.get('rd-pack-selector')) {
    class RDPackSelector extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';

        this.radios = Array.from(this.querySelectorAll('.rd-pdp-pack__native-radio'));
        this.onChange = this.onChange.bind(this);
        this.radios.forEach((radio) => radio.addEventListener('change', this.onChange));
        this.syncSelectedClass();
      }

      disconnectedCallback() {
        this.radios?.forEach((radio) => radio.removeEventListener('change', this.onChange));
      }

      onChange(event) {
        if (!event.target.checked) return;
        const option = event.target.closest('[data-rd-pack-option]');
        this.syncSelectedClass();
        this.syncVariantMedia(option);
        this.syncVariantUrl(option);
        this.emitSelection(option);
      }

      syncSelectedClass() {
        this.querySelectorAll('[data-rd-pack-option]').forEach((option) => {
          const radio = option.querySelector('.rd-pdp-pack__native-radio');
          option.classList.toggle('is-selected', Boolean(radio?.checked));
        });
      }

      syncVariantMedia(option) {
        const mediaId = option?.dataset.packMediaId;
        if (!mediaId) return;

        const gallery = this.closest('.rd-pdp-hero')?.querySelector('[data-rd-gallery]');
        if (!gallery) return;

        const thumb = Array.from(gallery.querySelectorAll('[data-rd-gallery-thumb]'))
          .find((item) => item.dataset.mediaId === mediaId);
        thumb?.click();
      }

      syncVariantUrl(option) {
        const variantId = option?.dataset.packVariantId;
        if (!variantId || !window.history?.replaceState) return;

        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', url.toString());
        } catch (_) {}
      }

      emitSelection(option) {
        if (!option) return;

        this.dispatchEvent(new CustomEvent('rd:pack-change', {
          bubbles: true,
          detail: {
            variantId: option.dataset.packVariantId || '',
            available: option.dataset.packAvailable === 'true',
            packCount: Number.parseInt(option.dataset.packCount || '1', 10) || 1,
            price: option.dataset.packPrice || '',
            compare: option.dataset.packCompare || '',
            saving: option.dataset.packSaving || '',
            note: option.dataset.packNote || ''
          }
        }));
      }
    }

    customElements.define('rd-pack-selector', RDPackSelector);
  }

  if (!customElements.get('rd-purchase-card')) {
    class RDPurchaseCard extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';

        this.infoRoot = this.closest('[data-rd-pdp-info]');
        this.form = this.querySelector('[data-rd-purchase-form]');
        this.variantId = this.querySelector('[data-rd-variant-id]');
        this.cartQuantity = this.querySelector('[data-rd-cart-quantity]');
        this.qtyDisplay = this.querySelector('[data-rd-qty-display]');
        this.minus = this.querySelector('[data-rd-qty-minus]');
        this.plus = this.querySelector('[data-rd-qty-plus]');
        this.addButton = this.querySelector('[data-rd-add-to-cart]');
        this.buyButton = this.querySelector('[data-rd-buy-now]');
        this.addLabel = this.querySelector('[data-rd-add-label]');
        this.buyLabel = this.querySelector('[data-rd-buy-label]');
        this.error = this.querySelector('[data-rd-purchase-error]');
        this.price = this.querySelector('[data-rd-purchase-price]');
        this.compare = this.querySelector('[data-rd-purchase-compare]');
        this.saving = this.querySelector('[data-rd-purchase-saving]');
        this.note = this.querySelector('[data-rd-purchase-note]');

        this.purchaseQuantity = 1;
        this.pack = {
          variantId: this.variantId?.value || '',
          available: this.addButton?.dataset.soldOut !== 'true',
          packCount: 1,
          price: this.price?.textContent?.trim() || '',
          compare: this.compare?.textContent?.trim() || '',
          saving: this.saving?.textContent?.trim() || '',
          note: this.note?.textContent?.trim() || ''
        };

        this.onPackChange = (event) => {
          this.pack = { ...this.pack, ...event.detail };
          this.purchaseQuantity = 1;
          this.updatePurchaseState();
        };
        this.onMinus = () => this.setPurchaseQuantity(this.purchaseQuantity - 1);
        this.onPlus = () => this.setPurchaseQuantity(this.purchaseQuantity + 1);
        this.onSubmit = (event) => {
          event.preventDefault();
          this.addToCart(false);
        };
        this.onBuyNow = () => this.addToCart(true);

        this.infoRoot?.addEventListener('rd:pack-change', this.onPackChange);
        this.minus?.addEventListener('click', this.onMinus);
        this.plus?.addEventListener('click', this.onPlus);
        this.form?.addEventListener('submit', this.onSubmit);
        this.buyButton?.addEventListener('click', this.onBuyNow);

        this.syncFromSelectedPack();
        this.updatePurchaseState();
      }

      disconnectedCallback() {
        this.infoRoot?.removeEventListener('rd:pack-change', this.onPackChange);
        this.minus?.removeEventListener('click', this.onMinus);
        this.plus?.removeEventListener('click', this.onPlus);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.buyButton?.removeEventListener('click', this.onBuyNow);
      }

      syncFromSelectedPack() {
        const selected = this.infoRoot
          ?.querySelector('[data-rd-pack-option] .rd-pdp-pack__native-radio:checked')
          ?.closest('[data-rd-pack-option]');

        if (!selected) return;

        this.pack = {
          variantId: selected.dataset.packVariantId || this.pack.variantId,
          available: selected.dataset.packAvailable === 'true',
          packCount: Number.parseInt(selected.dataset.packCount || '1', 10) || 1,
          price: selected.dataset.packPrice || this.pack.price,
          compare: selected.dataset.packCompare || '',
          saving: selected.dataset.packSaving || '',
          note: selected.dataset.packNote || ''
        };
      }

      setPurchaseQuantity(quantity) {
        this.purchaseQuantity = Math.max(1, Math.min(99, quantity));
        this.updatePurchaseState();
      }

      updatePurchaseState() {
        if (this.qtyDisplay) this.qtyDisplay.textContent = String(this.purchaseQuantity);
        if (this.cartQuantity) this.cartQuantity.value = String(this.purchaseQuantity);
        if (this.variantId && this.pack.variantId) this.variantId.value = this.pack.variantId;

        if (this.price) {
          this.price.textContent = this.multiplyMoneyText(this.pack.price, this.purchaseQuantity);
        }

        this.setOptionalText(
          this.compare,
          this.multiplyMoneyText(this.pack.compare, this.purchaseQuantity)
        );

        this.setOptionalText(
          this.saving,
          this.multiplyMoneyText(this.pack.saving, this.purchaseQuantity)
        );

        if (this.note) this.note.textContent = this.pack.note || '';

        const soldOut = !this.pack.available;
        [this.addButton, this.buyButton].forEach((button) => {
          if (!button) return;
          button.dataset.soldOut = soldOut ? 'true' : 'false';
          button.disabled = soldOut;
        });

        if (this.addLabel && this.addButton) {
          this.addLabel.textContent = soldOut
            ? 'Sold out'
            : (this.addButton.dataset.defaultLabel || 'Add to Basket');
        }

        if (this.buyLabel && this.buyButton) {
          this.buyLabel.textContent = soldOut
            ? 'Sold out'
            : (this.buyButton.dataset.defaultLabel || 'Buy It Now');
        }
      }

      setOptionalText(element, text) {
        if (!element) return;
        element.textContent = text || '';
        element.classList.toggle('is-hidden', !text);
      }

      multiplyMoneyText(text, multiplier) {
        if (!text || multiplier === 1) return text || '';

        const match = String(text).match(/^([^0-9]*)([0-9][0-9,]*(?:\.[0-9]+)?)(.*)$/);
        if (!match) return text;

        const value = Number.parseFloat(match[2].replace(/,/g, ''));
        if (!Number.isFinite(value)) return text;

        const multiplied = value * multiplier;
        const decimals = match[2].includes('.') ? match[2].split('.')[1].length : 0;
        const formatted = multiplied.toLocaleString('en-IN', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        });

        return `${match[1]}${formatted}${match[3]}`;
      }

      setBusy(isBusy) {
        [this.addButton, this.buyButton].forEach((button) => {
          if (!button) return;
          button.classList.toggle('is-loading', isBusy);
          button.toggleAttribute('aria-busy', isBusy);
          button.disabled = isBusy || button.dataset.soldOut === 'true';
        });
      }

      showError(message = '') {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = !message;
      }

      async addToCart(buyNow) {
        if (!this.form || this.addButton?.disabled || this.buyButton?.disabled) return;

        this.showError('');
        this.setBusy(true);

        try {
          const formData = new FormData(this.form);
          const cartDrawer = !buyNow ? document.querySelector('cart-drawer') : null;

          if (cartDrawer?.getSectionsToRender) {
            formData.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            cartDrawer.setActiveElement?.(document.activeElement);
          }

          const response = await fetch(cartAddUrl(), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });

          const data = await response.json();
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Unable to add this item to the cart.');
          }

          if (buyNow) {
            window.location.href = `${rootPath()}checkout`;
            return;
          }

          if (cartDrawer?.renderContents && data.sections) {
            cartDrawer.renderContents(data);
            this.setBusy(false);
            return;
          }

          window.location.href = cartUrl();
        } catch (error) {
          this.showError(error?.message || 'Unable to add this item to the cart.');
          this.setBusy(false);
        }
      }
    }

    customElements.define('rd-purchase-card', RDPurchaseCard);
  }
})();
